import ApiClient from '../ApiClient';
import { NiceResponse } from '../dto/NiceResponse';
import { LessonAssignment, AssignmentQuestion } from '../../models/assignments';
import {
  AssignmentCreateRequest,
  AssignmentUpdateRequest,
} from './dto/AssignmentRequests.dto';
import {
  QuestionRequest,
  QuestionsReorderRequest,
} from './dto/QuestionRequests.dto';

function isNiceError(e: unknown): e is { code?: number } {
  return typeof e === 'object' && e !== null && 'code' in e;
}

export class AssignmentsApi {
  // eslint-disable-next-line no-unused-vars
  constructor(private api: ApiClient) {}

  // Получить задание урока. 404 (задания нет) — штатный случай, вернём null.
  async getByLesson(lessonId: string): Promise<LessonAssignment | null> {
    try {
      const resp = await this.api.client.get<NiceResponse<LessonAssignment>>(
        `/courses/lessons/${lessonId}/assignment`,
      );
      return resp.data.body ?? null;
    } catch (e) {
      if (isNiceError(e) && e.code === 404) {
        return null;
      }
      throw e;
    }
  }

  // Создать задание к уроку.
  async create(lessonId: string, req: AssignmentCreateRequest) {
    const resp = await this.api.client.post<NiceResponse<LessonAssignment>>(
      `/courses/lessons/${lessonId}/assignment`,
      { ...req },
    );
    return resp.data;
  }

  // Обновить задание (частично — отсутствие поля значит "не менять").
  async update(id: string, req: AssignmentUpdateRequest) {
    const resp = await this.api.client.put<NiceResponse<string>>(
      `/courses/assignments/${id}`,
      { ...req },
    );
    return resp.data;
  }

  // Удалить задание.
  async delete(id: string) {
    const resp = await this.api.client.delete<NiceResponse<string>>(
      `/courses/assignments/${id}`,
    );
    return resp.data;
  }

  // Добавить вопрос к заданию.
  async addQuestion(assignmentId: string, req: QuestionRequest) {
    const resp = await this.api.client.post<NiceResponse<AssignmentQuestion>>(
      `/courses/assignments/${assignmentId}/questions`,
      { ...req },
    );
    return resp.data;
  }

  // Полная замена вопроса и его вариантов (id вариантов меняются).
  async updateQuestion(questionId: string, req: QuestionRequest) {
    const resp = await this.api.client.put<NiceResponse<AssignmentQuestion>>(
      `/courses/questions/${questionId}`,
      { ...req },
    );
    return resp.data;
  }

  // Удалить вопрос.
  async deleteQuestion(questionId: string) {
    const resp = await this.api.client.delete<NiceResponse<string>>(
      `/courses/questions/${questionId}`,
    );
    return resp.data;
  }

  // Изменить порядок вопросов задания.
  async reorderQuestions(assignmentId: string, req: QuestionsReorderRequest) {
    const resp = await this.api.client.put<NiceResponse<string>>(
      `/courses/assignments/${assignmentId}/questions/reorder`,
      { ...req },
    );
    return resp.data;
  }
}
